import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Filter, Heart, Calendar, MapPin, Eye, Edit, Trash2, Mic, Video, Camera, FileText, Grid, List } from 'lucide-react'
import Layout from '../common/Layout'
import { lifeEventAPI } from '../../config/lifeEventAPI'
import { getMediaServeUrl } from '../../config/mediaAPI'

function StoriesHub() {
  const navigate = useNavigate()
  const [stories, setStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedDate, setSelectedDate] = useState('all')
  const [viewMode, setViewMode] = useState('grid')

  const storyTypes = [
    { value: 'all', label: '📚 All Stories', icon: '📚' },
    { value: 'family', label: '❤️ Family Memories', icon: '❤️' },
    { value: 'childhood', label: '🧸 Childhood', icon: '🧸' },
    { value: 'career', label: '💼 Career', icon: '💼' },
    { value: 'travel', label: '✈️ Travel', icon: '✈️' },
    { value: 'recipes', label: '👩‍🍳 Recipes', icon: '👩‍🍳' },
    { value: 'other', label: '📝 Other', icon: '📝' }
  ]

  const dateFilters = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' }
  ]

  useEffect(() => {
    fetchStories()
  }, [])

  const fetchStories = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch stories using the life event API
      const response = await lifeEventAPI.getEvents()
      
      // Handle different response structures
      let allEvents = []
      if (response.results) {
        allEvents = response.results
      } else if (Array.isArray(response)) {
        allEvents = response
      } else {
        allEvents = [response]
      }
      
      // Filter to only show story-type events (template 12 or has story_type)
      const storyEvents = allEvents.filter(event => {
        const isStoryTemplate = event.template === 12
        const hasStoryType = event.event_data?.story_type || event.story_type
        const hasStoryContent = event.story || event.story_responses?.what_happened
        
        return isStoryTemplate || hasStoryType || hasStoryContent
      })
      
      setStories(storyEvents)
    } catch (err) {
      console.error('Error fetching stories:', err)
      setError('Failed to load stories. Please try again.')
      
      // Set mock data for development/demo purposes
      setStories(getMockStories())
    } finally {
      setLoading(false)
    }
  }

  const getMockStories = () => {
    return [
      {
        id: 1,
        title: "Grandma's Secret Cookie Recipe",
        story_type: "Family Memory",
        date: "2024-01-15",
        location: "Grandma's Kitchen",
        story: "The day Grandma finally shared her famous chocolate chip cookie recipe with me...",
        media_files: [
          { 
            id: 1,
            media_type: "image", 
            title: "Cookie Recipe",
            description: "Grandma's famous cookies"
          }
        ],
        is_private: false,
        created_at: "2024-01-15T10:30:00Z"
      },
      {
        id: 2,
        title: "The Lost Vacation Adventure",
        story_type: "Funny Moment",
        date: "2023-08-20",
        location: "Mountain Trail",
        story: "When Dad insisted he knew the way and we ended up completely lost...",
        media_files: [
          { 
            id: 2,
            media_type: "video", 
            title: "Vacation Video",
            description: "Our mountain adventure"
          },
          { 
            id: 3,
            media_type: "image", 
            title: "Mountain View",
            description: "Beautiful scenery"
          }
        ],
        is_private: false,
        created_at: "2023-08-20T14:15:00Z"
      },
      {
        id: 3,
        title: "My First Day at Work",
        story_type: "Personal Experience",
        date: "2023-06-10",
        location: "Office Building",
        story: "Nervous and excited, I walked into my first real job...",
        media_files: [
          { 
            id: 4,
            media_type: "audio", 
            title: "First Day Audio",
            description: "My thoughts on the first day"
          }
        ],
        is_private: true,
        created_at: "2023-06-10T09:00:00Z"
      },
      {
        id: 4,
        title: "Sarah's Graduation Day",
        story_type: "Celebration",
        date: "2023-05-25",
        location: "University Campus",
        story: "Watching our daughter walk across the stage was one of our proudest moments...",
        media_files: [
          { 
            id: 5,
            media_type: "image", 
            title: "Graduation Photo 1",
            description: "Sarah in her cap and gown"
          },
          { 
            id: 6,
            media_type: "image", 
            title: "Graduation Photo 2",
            description: "Family celebration"
          },
          { 
            id: 7,
            media_type: "image", 
            title: "Graduation Photo 3",
            description: "Cake cutting"
          },
          { 
            id: 8,
            media_type: "image", 
            title: "Graduation Photo 4",
            description: "Group photo"
          }
        ],
        is_private: false,
        created_at: "2023-05-25T16:30:00Z"
      },
      {
        id: 5,
        title: "The Neighbor Who Changed Everything",
        story_type: "Life Lesson",
        date: "2022-12-03",
        location: "Our Neighborhood",
        story: "Sometimes the most important lessons come from unexpected places...",
        media_files: [],
        is_private: false,
        created_at: "2022-12-03T11:45:00Z"
      },
      {
        id: 6,
        title: "Sunday Dinners at Home",
        story_type: "Family Memory",
        date: "2022-09-18",
        location: "Family Home",
        story: "Every Sunday, the whole family would gather around the table...",
        media_files: [
          { 
            id: 9,
            media_type: "image", 
            title: "Sunday Dinner",
            description: "Family around the table"
          }
        ],
        is_private: false,
        created_at: "2022-09-18T18:00:00Z"
      }
    ]
  }

  const filteredStories = stories.filter(story => {
    const storyTitle = story.title || ''
    const storyContent = story.story || story.story_responses?.what_happened || ''
    const storyLocation = story.location || ''
    
    const matchesSearch = storyTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         storyContent.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         storyLocation.toLowerCase().includes(searchTerm.toLowerCase())
    
    const storyType = story.story_type || story.event_data?.story_type || ''
    const matchesType = selectedType === 'all' || 
                       storyType.toLowerCase().includes(selectedType.toLowerCase())
    
    const storyDate = story.date || story.created_at
    const matchesDate = selectedDate === 'all' || filterByDate(storyDate, selectedDate)
    
    return matchesSearch && matchesType && matchesDate
  })

  const filterByDate = (storyDate, filterType) => {
    if (!storyDate) return false
    
    const storyDateObj = new Date(storyDate)
    const now = new Date()
    
    switch (filterType) {
      case 'today':
        return storyDateObj.toDateString() === now.toDateString()
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        return storyDateObj >= weekAgo
      case 'month':
        return storyDateObj.getMonth() === now.getMonth() && 
               storyDateObj.getFullYear() === now.getFullYear()
      case 'year':
        return storyDateObj.getFullYear() === now.getFullYear()
      default:
        return true
    }
  }

  const getStoryTypeIcon = (storyType) => {
    const type = storyTypes.find(t => 
      t.label.toLowerCase().includes(storyType?.toLowerCase() || '')
    )
    return type?.icon || '📝'
  }

  const getMediaIcon = (mediaType) => {
    switch (mediaType) {
      case 'image': return <Camera className="w-4 h-4" />
      case 'video': return <Video className="w-4 h-4" />
      case 'audio': return <Mic className="w-4 h-4" />
      default: return <FileText className="w-4 h-4" />
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const handleCreateStory = () => {
    navigate('/tell-a-story')
  }

  const handleViewStory = (storyId) => {
    navigate(`/stories/${storyId}`)
  }

  const handleEditStory = (storyId) => {
    navigate(`/stories/${storyId}/edit`)
  }

  const handleDeleteStory = async (storyId) => {
    if (window.confirm('Are you sure you want to delete this story? This action cannot be undone.')) {
      try {
        await lifeEventAPI.deleteEvent(storyId)
        setStories(stories.filter(story => story.id !== storyId))
      } catch (err) {
        console.error('Error deleting story:', err)
        alert('Failed to delete story. Please try again.')
      }
    }
  }

  const getRecentStories = () => {
    return filteredStories.slice(0, 3)
  }

  const getAllStories = () => {
    return filteredStories.slice(3)
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#556B2F] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your family stories...</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      {/* Header with gradient background */}
      <header className="bg-gradient-to-br from-[#556B2F] to-[#6B8E23] text-white py-16 px-8 text-center shadow-lg">
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-shadow-lg">
          📚 Your Family Stories
        </h1>
        <p className="text-xl opacity-95 mb-8 font-normal">
          Discover, share, and preserve your family's precious memories
        </p>
        <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 inline-block border border-white/30">
          <button
            onClick={handleCreateStory}
            className="bg-[#E65100] hover:bg-[#D84315] text-white px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 hover:-translate-y-1 shadow-lg flex items-center gap-3 mx-auto"
          >
            ✨ Share a New Memory
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Quick Actions Panel */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-8">
          <h2 className="text-2xl font-semibold text-[#556B2F] mb-6 text-center">
            💡 What would you like to do today?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-[#F8F9FA] to-[#E8F5E8] border-2 border-gray-200 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-[#556B2F] hover:shadow-lg text-center">
              <div className="text-4xl mb-3">❓</div>
              <div className="font-semibold text-gray-800 mb-2">Answer a Prompt</div>
              <div className="text-sm text-gray-600">We'll ask you questions to help tell your story</div>
            </div>
            <div className="bg-gradient-to-br from-[#F8F9FA] to-[#E8F5E8] border-2 border-gray-200 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-[#556B2F] hover:shadow-lg text-center">
              <div className="text-4xl mb-3">📸</div>
              <div className="font-semibold text-gray-800 mb-2">Share Photos</div>
              <div className="text-sm text-gray-600">Upload photos and tell us about them</div>
            </div>
            <div className="bg-gradient-to-br from-[#F8F9FA] to-[#E8F5E8] border-2 border-gray-200 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-[#556B2F] hover:shadow-lg text-center">
              <div className="text-4xl mb-3">🎤</div>
              <div className="font-semibold text-gray-800 mb-2">Record Your Voice</div>
              <div className="text-sm text-gray-600">Tell your story using your own voice</div>
            </div>
            <div className="bg-gradient-to-br from-[#F8F9FA] to-[#E8F5E8] border-2 border-gray-200 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-[#556B2F] hover:shadow-lg text-center">
              <div className="text-4xl mb-3">👩‍🍳</div>
              <div className="font-semibold text-gray-800 mb-2">Share a Recipe</div>
              <div className="text-sm text-gray-600">Pass down your favorite family recipes</div>
            </div>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            <div className="relative">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl">🔍</span>
              <input
                type="text"
                placeholder="Search your stories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-3 border-gray-300 rounded-xl text-lg transition-all duration-300 focus:outline-none focus:border-[#556B2F] focus:ring-4 focus:ring-[#556B2F]/10 bg-gray-50 focus:bg-white"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-4 border-3 border-gray-300 rounded-xl text-lg cursor-pointer transition-all duration-300 focus:outline-none focus:border-[#556B2F] appearance-none bg-white"
            >
              {storyTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <div className="flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex-1 py-3 px-4 rounded-lg text-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                  viewMode === 'grid' 
                    ? 'bg-[#556B2F] text-white shadow-md' 
                    : 'bg-transparent text-gray-600'
                }`}
              >
                <Grid className="w-5 h-5" />
                Cards
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex-1 py-3 px-4 rounded-lg text-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                  viewMode === 'list' 
                    ? 'bg-[#556B2F] text-white shadow-md' 
                    : 'bg-transparent text-gray-600'
                }`}
              >
                <List className="w-5 h-5" />
                List
              </button>
            </div>
          </div>
        </div>

        {/* Recent Stories Section */}
        {getRecentStories().length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <h2 className="text-3xl font-semibold text-[#556B2F]">🕒 Recently Added</h2>
              <span className="bg-[#E8F5E8] text-[#2E7D32] px-4 py-2 rounded-full text-lg font-medium">
                {getRecentStories().length} new stories
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getRecentStories().map(story => (
                <div key={story.id} className="bg-gradient-to-br from-[#F0F8F0] to-[#E8F5E8] border-2 border-[#C8E6C9] rounded-xl p-6 relative transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="absolute -top-2 right-6 bg-[#4CAF50] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    NEW
                  </div>
                  {/* Story Image */}
                  <div className="w-full h-40 rounded-lg mb-4 overflow-hidden">
                    {story.media_files && story.media_files.length > 0 ? (
                      story.media_files.length === 1 ? (
                        // Single image
                        story.media_files[0].media_type === 'image' ? (
                          <img 
                            src={getMediaServeUrl(story.media_files[0].id)}
                            alt={story.media_files[0].title || `Photo from ${story.title}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                            onLoad={() => {}}
                          />
                        ) : story.media_files[0].media_type === 'video' ? (
                          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                            <div className="text-6xl opacity-50">🎥</div>
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                              <div className="bg-black/70 text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl">
                                ▶️
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                            <div className="text-6xl opacity-50">🎵</div>
                          </div>
                        )
                      ) : story.media_files.length === 2 ? (
                        // Two images - side by side
                        <div className="grid grid-cols-2 gap-1 h-full">
                          {story.media_files.slice(0, 2).map((media, index) => (
                            <div key={index} className="relative">
                              {media.media_type === 'image' ? (
                                <img 
                                  src={getMediaServeUrl(media.id)}
                                  alt={media.title || `Photo ${index + 1} from ${story.title}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                  onLoad={() => {}}
                                />
                              ) : media.media_type === 'video' ? (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                                  <div className="text-3xl opacity-50">🎥</div>
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="bg-black/70 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">
                                      ▶️
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <div className="text-3xl opacity-50">🎵</div>
                                </div>
                              )}
                              
                              {/* Fallback icon if media fails to load */}
                              <div className="hidden absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <div className="text-3xl opacity-50">📷</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : story.media_files.length === 3 ? (
                        // Three images - 3x1 layout
                        <div className="grid grid-cols-3 gap-1 h-full">
                          {story.media_files.slice(0, 3).map((media, index) => (
                            <div key={index} className="relative">
                              {media.media_type === 'image' ? (
                                <img 
                                  src={getMediaServeUrl(media.id)}
                                  alt={media.title || `Photo ${index + 1} from ${story.title}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                  onLoad={() => {}}
                                />
                              ) : media.media_type === 'video' ? (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                                  <div className="text-2xl opacity-50">🎥</div>
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="bg-black/70 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                                      ▶️
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <div className="text-2xl opacity-50">🎵</div>
                                </div>
                              )}
                              
                              {/* Fallback icon if media fails to load */}
                              <div className="hidden absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <div className="text-2xl opacity-50">📷</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : story.media_files.length === 4 ? (
                        // Four images - 2x2 grid
                        <div className="grid grid-cols-2 gap-1 h-full">
                          {story.media_files.slice(0, 4).map((media, index) => (
                            <div key={index} className="relative">
                              {media.media_type === 'image' ? (
                                <img 
                                  src={getMediaServeUrl(media.id)}
                                  alt={media.title || `Photo ${index + 1} from ${story.title}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                  onLoad={() => {}}
                                />
                              ) : media.media_type === 'video' ? (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                                  <div className="text-2xl opacity-50">🎥</div>
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="bg-black/70 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                                      ▶️
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <div className="text-2xl opacity-50">🎵</div>
                                </div>
                              )}
                              
                              {/* Fallback icon if media fails to load */}
                              <div className="hidden absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <div className="text-2xl opacity-50">📷</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : story.media_files.length > 4 ? (
                        // Five or more images - 3x3 grid with overlay
                        <div className="grid grid-cols-3 gap-1 h-full">
                          {story.media_files.slice(0, 9).map((media, index) => (
                            <div key={index} className="relative">
                              {media.media_type === 'image' ? (
                                <img 
                                  src={getMediaServeUrl(media.id)}
                                  alt={media.title || `Photo ${index + 1} from ${story.title}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                  onLoad={() => {}}
                                />
                              ) : media.media_type === 'video' ? (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                                  <div className="text-xl opacity-50">🎥</div>
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="bg-black/70 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">
                                      ▶️
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <div className="text-xl opacity-50">🎵</div>
                                </div>
                              )}
                              
                              {/* Fallback icon if media fails to load */}
                              <div className="hidden absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <div className="text-xl opacity-50">📷</div>
                              </div>
                              
                              {/* Overlay for the 9th image if there are more than 9 */}
                              {index === 8 && story.media_files.length > 9 && (
                                <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-white font-semibold text-xs">
                                  +{story.media_files.length - 9} more
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : null
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <div className="text-6xl opacity-50">📷</div>
                      </div>
                    )}
                    
                    {/* Fallback icon if media fails to load */}
                    <div className="hidden w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <div className="text-6xl opacity-50">📷</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{getStoryTypeIcon(story.story_type)}</span>
                    <span className="bg-[#E8F5E8] text-[#2E7D32] px-3 py-1 rounded-full text-sm font-medium">
                      {story.story_type || 'Story'}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3 line-clamp-2">
                    {story.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {story.story || story.story_responses?.what_happened || 'No description available'}
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 border-l-4 border-[#556B2F]">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#556B2F] w-5">📅</span>
                      <span className="text-gray-600">{formatDate(story.date)}</span>
                    </div>
                    {story.location && (
                      <div className="flex items-center gap-3">
                        <span className="text-[#556B2F] w-5">📍</span>
                        <span className="text-gray-600">{story.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleViewStory(story.id)}
                      className="flex-1 bg-[#556B2F] hover:bg-[#4A5D23] text-white py-3 rounded-lg font-medium transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2"
                    >
                      👁️ Read Story
                    </button>
                    <button
                      onClick={() => handleEditStory(story.id)}
                      className="flex-1 bg-white border-2 border-gray-300 text-[#556B2F] hover:bg-[#E8F5E8] hover:border-[#556B2F] py-3 rounded-lg font-medium transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2"
                    >
                      ✏️ Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Stories Section */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <h2 className="text-3xl font-semibold text-[#556B2F]">📖 All Your Stories</h2>
            <span className="bg-[#E8F5E8] text-[#2E7D32] px-4 py-2 rounded-full text-lg font-medium">
              {filteredStories.length} stories total
            </span>
          </div>
          
          {error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">{error}</div>
              <button
                onClick={fetchStories}
                className="bg-[#556B2F] text-white px-6 py-3 rounded-lg hover:bg-[#6B8E23] transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : filteredStories.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">
                {searchTerm || selectedType !== 'all' || selectedDate !== 'all' 
                  ? 'No stories match your current filters.' 
                  : 'No stories found. Create your first family story!'}
              </div>
              {searchTerm || selectedType !== 'all' || selectedDate !== 'all' ? (
                <button
                  onClick={() => {
                    setSearchTerm('')
                    setSelectedType('all')
                    setSelectedDate('all')
                  }}
                  className="text-white hover:underline"
                >
                  Clear all filters
                </button>
              ) : (
                <button
                  onClick={handleCreateStory}
                  className="bg-[#E65100] text-white px-6 py-3 rounded-lg hover:bg-[#FF7043] transition-colors"
                >
                  Create Your First Story
                </button>
              )}
            </div>
          ) : (
            <div className={`grid gap-8 ${
              viewMode === 'list' 
                ? 'grid-cols-1' 
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {getAllStories().map(story => (
                <div
                  key={story.id}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-pointer"
                >
                  {/* Story Image Container */}
                  <div className="relative w-full h-52 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    {story.media_files && story.media_files.length > 0 ? (
                      story.media_files.length === 1 ? (
                        // Single image
                        story.media_files[0].media_type === 'image' ? (
                          <img 
                            src={getMediaServeUrl(story.media_files[0].id)}
                            alt={story.media_files[0].title || `Photo from ${story.title}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                            onLoad={() => {}}
                          />
                        ) : story.media_files[0].media_type === 'video' ? (
                          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                            <div className="text-6xl opacity-50">🎥</div>
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                              <div className="bg-black/70 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl">
                                ▶️
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                            <div className="text-6xl opacity-50">🎵</div>
                          </div>
                        )
                      ) : story.media_files.length === 2 ? (
                        // Two images - side by side
                        <div className="grid grid-cols-2 gap-1 h-full">
                          {story.media_files.slice(0, 2).map((media, index) => (
                            <div key={index} className="relative">
                              {media.media_type === 'image' ? (
                                <img 
                                  src={getMediaServeUrl(media.id)}
                                  alt={media.title || `Photo ${index + 1} from ${story.title}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                  onLoad={() => {}}
                                />
                              ) : media.media_type === 'video' ? (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                                  <div className="text-4xl opacity-50">🎥</div>
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">
                                      ▶️
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <div className="text-4xl opacity-50">🎵</div>
                                </div>
                              )}
                              
                              {/* Fallback icon if media fails to load */}
                              <div className="hidden absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <div className="text-4xl opacity-50">📷</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : story.media_files.length === 3 ? (
                        // Three images - 3x1 layout
                        <div className="grid grid-cols-3 gap-1 h-full">
                          {story.media_files.slice(0, 3).map((media, index) => (
                            <div key={index} className="relative">
                              {media.media_type === 'image' ? (
                                <img 
                                  src={getMediaServeUrl(media.id)}
                                  alt={media.title || `Photo ${index + 1} from ${story.title}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                  onLoad={() => {}}
                                />
                              ) : media.media_type === 'video' ? (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                                  <div className="text-3xl opacity-50">🎥</div>
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="bg-black/70 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">
                                      ▶️
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <div className="text-3xl opacity-50">🎵</div>
                                </div>
                              )}
                              
                              {/* Fallback icon if media fails to load */}
                              <div className="hidden absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <div className="text-3xl opacity-50">📷</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : story.media_files.length === 4 ? (
                        // Four images - 2x2 grid
                        <div className="grid grid-cols-2 gap-1 h-full">
                          {story.media_files.slice(0, 4).map((media, index) => (
                            <div key={index} className="relative">
                              {media.media_type === 'image' ? (
                                <img 
                                  src={getMediaServeUrl(media.id)}
                                  alt={media.title || `Photo ${index + 1} from ${story.title}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                  onLoad={() => {}}
                                />
                              ) : media.media_type === 'video' ? (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                                  <div className="text-3xl opacity-50">🎥</div>
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="bg-black/70 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">
                                      ▶️
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <div className="text-3xl opacity-50">🎵</div>
                                </div>
                              )}
                              
                              {/* Fallback icon if media fails to load */}
                              <div className="hidden absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <div className="text-3xl opacity-50">📷</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : story.media_files.length > 4 ? (
                        // Five or more images - 3x3 grid with overlay
                        <div className="grid grid-cols-3 gap-1 h-full">
                          {story.media_files.slice(0, 9).map((media, index) => (
                            <div key={index} className="relative">
                              {media.media_type === 'image' ? (
                                <img 
                                  src={getMediaServeUrl(media.id)}
                                  alt={media.title || `Photo ${index + 1} from ${story.title}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                  onLoad={() => {}}
                                />
                              ) : media.media_type === 'video' ? (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative">
                                  <div className="text-2xl opacity-50">🎥</div>
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="bg-black/70 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                                      ▶️
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <div className="text-2xl opacity-50">🎵</div>
                                </div>
                              )}
                              
                              {/* Fallback icon if media fails to load */}
                              <div className="hidden absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <div className="text-2xl opacity-50">📷</div>
                              </div>
                              
                              {/* Overlay for the 9th image if there are more than 9 */}
                              {index === 8 && story.media_files.length > 9 && (
                                <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-white font-semibold text-xs">
                                  +{story.media_files.length - 9} more
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : null
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                        <div className="text-6xl mb-2 opacity-50">📝</div>
                        <div className="text-sm opacity-70">Text Story</div>
                      </div>
                    )}
                    
                    {/* Debug info - remove this after fixing */}
                    {story.media_files && story.media_files.length > 0 && (
                      <div className="absolute top-3 left-3 bg-blue-500 text-white px-2 py-1 rounded text-xs opacity-75">
                        Debug: {story.media_files.length} files
                      </div>
                    )}
                    
                    {/* Media Count Badge */}
                    {story.media_files && story.media_files.length > 0 && (
                      <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-md flex items-center gap-1">
                        {story.media_files.some(m => m.media_type === 'video') ? '🎥' : '📷'} {story.media_files.length} {story.media_files.length === 1 ? 'item' : 'items'}
                      </div>
                    )}
                    
                    {/* Video Indicator */}
                    {story.media_files && story.media_files.some(m => m.media_type === 'video') && (
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/70 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl backdrop-blur-md">
                        ▶️
                      </div>
                    )}
                  </div>

                  {/* Story Content */}
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">{getStoryTypeIcon(story.story_type)}</span>
                      <span className="bg-[#E8F5E8] text-[#2E7D32] px-3 py-1 rounded-full text-sm font-medium">
                        {story.story_type || 'Story'}
                      </span>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mb-4 line-clamp-2 min-h-[3.6rem]">
                      {story.title}
                    </h3>

                    <p className="text-gray-600 mb-6 line-clamp-3 min-h-[4.8rem]">
                      {story.story || story.story_responses?.what_happened || 'No description available'}
                    </p>

                    {/* Story Meta */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6 border-l-4 border-[#556B2F]">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[#556B2F] w-5">📅</span>
                        <span className="text-gray-600">{formatDate(story.date)}</span>
                      </div>
                      {story.location && (
                        <div className="flex items-center gap-3">
                          <span className="text-[#556B2F] w-5">📍</span>
                          <span className="text-gray-600">{story.location}</span>
                        </div>
                      )}
                    </div>

                    {/* Story Actions */}
                    <div className="flex gap-3 mt-auto">
                      <button
                        onClick={() => handleViewStory(story.id)}
                        className="flex-1 bg-[#556B2F] hover:bg-[#4A5D23] text-white py-3 rounded-lg font-medium transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2"
                      >
                        👁️ Read Story
                      </button>
                      <button
                        onClick={() => handleEditStory(story.id)}
                        className="flex-1 bg-white border-2 border-gray-300 text-[#556B2F] hover:bg-[#E8F5E8] hover:border-[#556B2F] py-3 rounded-lg font-medium transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2"
                      >
                        ✏️ Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredStories.length > 6 && (
          <div className="flex justify-center items-center gap-4 py-8">
            <button className="px-5 py-3 border-2 border-gray-300 bg-white rounded-lg cursor-pointer text-lg transition-all duration-300 hover:border-[#556B2F] hover:bg-gray-50">
              ‹ Previous
            </button>
            <button className="px-5 py-3 border-2 border-[#556B2F] bg-[#556B2F] text-white rounded-lg cursor-pointer text-lg">
              1
            </button>
            <button className="px-5 py-3 border-2 border-gray-300 bg-white rounded-lg cursor-pointer text-lg transition-all duration-300 hover:border-[#556B2F] hover:bg-gray-50">
              2
            </button>
            <button className="px-5 py-3 border-2 border-gray-300 bg-white rounded-lg cursor-pointer text-lg transition-all duration-300 hover:border-[#556B2F] hover:bg-gray-50">
              3
            </button>
            <button className="px-5 py-3 border-2 border-gray-300 bg-white rounded-lg cursor-pointer text-lg transition-all duration-300 hover:border-[#556B2F] hover:bg-gray-50">
              Next ›
            </button>
          </div>
        )}
      </div>

      {/* Floating Add Button */}
      <button
        onClick={handleCreateStory}
        className="fixed bottom-8 right-8 bg-[#E65100] text-white border-none w-16 h-16 rounded-full text-3xl cursor-pointer shadow-lg transition-all duration-300 hover:scale-110 z-50"
        title="Create a new story"
      >
        +
      </button>
    </Layout>
  )
}

export default StoriesHub 